import { useCallback } from 'react';
import {
  Typography,
  Button,
  makeStyles,
  fade,
  CircularProgress,
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import GetAppIcon from '@material-ui/icons/GetApp';
import ImageIcon from '@material-ui/icons/Image';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';

const useStyles = makeStyles((theme) => ({
  bottomRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    [theme.breakpoints.up('lg')]: {
      display: 'block',
      flexDirection: 'column',
    },
  },
  bottomRowControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    width: '100%',

    [theme.breakpoints.up('lg')]: {
      display: 'block',
    },
  },
  controlGroup: {
    flex: '0 0 auto',
    paddingBottom: theme.spacing(0),
    borderBottom: 'none',
    marginBottom: theme.spacing(0),
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    color: '#9196A6',
    marginBottom: theme.spacing(1.5),
  },
  chartExportButtonsContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),

    [theme.breakpoints.up('lg')]: {
      gap: theme.spacing(0),
      flexDirection: 'column',
    },
  },
  chartExportButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  chartExportButton: {
    flex: 1,
    background: 'white',
    color: theme.colors.lightBlue,
    border: `1px solid ${theme.colors.lightBlue}`,
    fontWeight: 600,
    padding: '10px 16px',
    borderRadius: 8,
    textTransform: 'none',
    fontSize: 13,
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    minWidth: 80,
    '&:hover:not(:disabled)': {
      backgroundColor: fade(theme.colors.lightBlue, 0.08),
      borderColor: '#0090E0',
      color: '#0090E0',
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      color: '#ccc',
      borderColor: '#ccc',
      cursor: 'not-allowed',
      backgroundColor: '#f9f9f9',
    },
  },
  exportButton: {
    background: theme.colors.lightBlue,
    color: 'white',
    fontWeight: 600,
    padding: '12px 24px',
    borderRadius: 8,
    textTransform: 'none',
    fontSize: 14,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    minWidth: 160,
    width: 'auto',
    [theme.breakpoints.up('lg')]: {
      width: '100%',
    },
    '&:hover:not(:disabled)': {
      backgroundColor: '#0090E0',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0, 168, 255, 0.25)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 2px 8px rgba(0, 168, 255, 0.2)',
    },
  },
}));

const ExportButtons = ({
  onExport,
  onExportChart,
  showTitle = false,
  isPngExporting = false,
  isPdfExporting = false,
  isDataExporting = false,
}) => {
  const classes = useStyles();
  const { en: { btn, populationHealth } } = useSelector((state) => state.i18n);


  const handlePngExport = useCallback(() => {
    onExportChart && onExportChart('png');
  }, [onExportChart]);

  const handlePdfExport = useCallback(() => {
    onExportChart && onExportChart('pdf');
  }, [onExportChart]);

  return (
    <div className={classes.bottomRow}>
      <div className={classes.bottomRowControls}>
        <div className={classes.controlGroup}>
          {showTitle && (
            <Typography className={classes.sectionTitle}>
              {populationHealth?.exportFile || 'Export file'}
            </Typography>
          )}

          <div className={classes.chartExportButtonsContainer}>
            <div className={classes.chartExportButtons}>
              <Button
                className={classes.chartExportButton}
                startIcon={
                  isPngExporting ? (
                    <CircularProgress size={16} />
                  ) : (
                    <ImageIcon style={{ fontSize: 16 }} />
                  )
                }
                onClick={handlePngExport}
                disabled={!onExportChart || isPngExporting || isPdfExporting}
              >
                PNG
              </Button>
              <Button
                className={classes.chartExportButton}
                startIcon={
                  isPdfExporting ? (
                    <CircularProgress size={16} />
                  ) : (
                    <PictureAsPdfIcon style={{ fontSize: 16 }} />
                  )
                }
                onClick={handlePdfExport}
                disabled={!onExportChart || isPngExporting || isPdfExporting}
              >
                PDF
              </Button>
            </div>

            <Button
              className={classes.exportButton}
              startIcon={
                isDataExporting ? (
                  <CircularProgress size={18} style={{ color: 'white' }} />
                ) : (
                  <GetAppIcon style={{ fontSize: 18 }} />
                )
              }
              onClick={onExport}
              disabled={isDataExporting}
            >
              {isDataExporting ? 'Exporting...' : `${btn?.export || 'Export'} Data`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportButtons;
